// pages/tasks/tatUtils.js
//
// Pure formatting helpers for the TAT column -- reuse the EXISTING
// `due_at` value already computed and persisted server-side at task
// creation (services/taskCreationService.js / controllers/taskController.js
// -- computeDueAtFromBusinessTat, the same business-hours TAT engine the
// Create Ticket/Raise Complaint form's own "Estimated TAT" preview already
// shows). This module invents NO new TAT calculation -- it only formats
// due_at/status/completed_at (all already present on every task row) into a
// short label + a tone for styling. `now` is an injectable parameter
// (defaults to `new Date()`) purely so tests can pin a fixed clock.
//
// Completed-task comparison timestamp: `completed_at` (set once, exactly
// when status transitions to "completed" -- taskController.js's status-
// update endpoint -- and cleared if moved away from it) is used in
// preference to `updated_at`, because `updated_at` is also touched by the
// same status-update statement on every future status change, so it is not
// a stable "when did this actually finish" marker. `completed_at` is not
// touched by posting a remark/chat message (those only INSERT into
// task_remarks). `updated_at` is kept only as a last-resort fallback for
// legacy rows created before `completed_at` existed.
const DONE_STATUSES = new Set(["completed", "closed"]);

export function formatDuration(ms) {
  const totalMinutes = Math.max(0, Math.round(Math.abs(ms) / 60000));
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export function getTatInfo(row, now = new Date()) {
  if (!row?.due_at) return { label: "—", tone: "gray" };
  const due = new Date(row.due_at);
  if (Number.isNaN(due.getTime())) return { label: "—", tone: "gray" };

  const status = String(row.status || "").toLowerCase();
  if (DONE_STATUSES.has(status)) {
    const completedSource = row.completed_at || row.updated_at;
    const completedAt = completedSource ? new Date(completedSource) : null;
    if (completedAt && !Number.isNaN(completedAt.getTime())) {
      return completedAt <= due
        ? { label: "Completed within TAT", tone: "green" }
        : { label: `TAT Breached +${formatDuration(completedAt - due)}`, tone: "red" };
    }
    // No completion timestamp available to compare against due_at --
    // report the plain outcome rather than guessing.
    return { label: "Completed", tone: "green" };
  }

  const diffMs = due.getTime() - now.getTime();
  if (diffMs < 0) return { label: `TAT Breached +${formatDuration(diffMs)}`, tone: "red" };
  const hours = diffMs / 3600000;
  if (hours <= 2) return { label: `${formatDuration(diffMs)} left`, tone: "amber" };
  return { label: `${formatDuration(diffMs)} left`, tone: "green" };
}

export function truncate(text, max = 40) {
  const s = String(text || "");
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}
